import {
  Button,
  IconButton,
  SelectChangeEvent,
  SelectOption,
  SimpleSelect,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@chronosphereio/chrono-ui';
import { useDebouncedState } from '@chronosphereio/shared';
import { produce } from 'immer';
import { DeleteOutline as DeleteIcon, Plus as PlusIcon } from 'mdi-material-ui';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { OverrideMatcherType, parseOverrideAlias, StringFormatOverride, StringFormatOverrides } from '@/core';
import { Color, TextAndBackgroundColorPicker } from '@/plugin-editor-components';

interface OptionType extends SelectOption {
  key: OverrideMatcherType;
}

const MATCH_OPTIONS: OptionType[] = [
  { label: 'is', key: 'ByName' },
  {
    label: 'matches regex',
    key: 'ByRegex',
  },
];

interface StringColumnSingleOverrideEditorProps {
  override: StringFormatOverride;
  onOverrideChange: (newOverride: StringFormatOverride) => void;
  onDelete: () => void;
}

export const StringColumnSingleOverrideEditor = ({
  override,
  onOverrideChange,
  onDelete,
}: StringColumnSingleOverrideEditorProps) => {
  const handleValueChange = useCallback(
    (newValue: string) => {
      onOverrideChange(
        produce(override, (draftOverride) => {
          draftOverride.value = newValue;
        })
      );
    },
    [onOverrideChange, override]
  );

  const handleMessageChange = useCallback(
    (newMessage: string) => {
      onOverrideChange(
        produce(override, (draftOverride) => {
          draftOverride.properties.name = newMessage;
        })
      );
    },
    [onOverrideChange, override]
  );

  // We hold a local state value for these values but we debounce them
  // to actually update upstream to avoid constant changes
  const [localOverrideValue, setLocalOverrideValue] = useDebouncedState(override.value, handleValueChange, 400);
  const [localMessage, setLocalMessage] = useDebouncedState(override.properties.name || '', handleMessageChange, 400);

  const handleLocalValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      setLocalOverrideValue(value);
    },
    [setLocalOverrideValue]
  );

  const handleLocalMessageChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      setLocalMessage(value);
    },
    [setLocalMessage]
  );

  const color = useMemo(
    () => ({
      color: override.properties.color,
      background: override.properties.background_color,
    }),
    [override.properties.color, override.properties.background_color]
  );

  const handleColorChange = useCallback(
    (newColor: Color) => {
      onOverrideChange(
        produce(override, (overrideDraft) => {
          overrideDraft.properties.color = newColor.color;
          overrideDraft.properties.background_color = newColor.background;

          if (overrideDraft.properties.color === undefined) {
            delete overrideDraft.properties.color;
          }

          if (overrideDraft.properties.background_color === undefined) {
            delete overrideDraft.properties.background_color;
          }
        })
      );
    },
    [override, onOverrideChange]
  );

  const handleMatcherTypeChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const type = event.target.value as OverrideMatcherType;
      onOverrideChange(
        produce(override, (overrideDraft) => {
          overrideDraft.matcher = { type };
        })
      );
    },
    [onOverrideChange, override]
  );

  const overrideType = override.matcher?.type || 'ByName';
  const regexError = useMemo(() => {
    if (overrideType === 'ByRegex') {
      let error = undefined;
      try {
        parseOverrideAlias(localOverrideValue);
      } catch (e) {
        error = e;
      }

      return error;
    }
  }, [overrideType, localOverrideValue]);

  return (
    <Stack direction="row" flexWrap="nowrap" alignItems="center" gap={5}>
      <Stack direction="row" flexWrap="nowrap" alignItems="center" gap={1} flexGrow={1}>
        <div style={{ flexGrow: 0 }}>When value</div>
        <div style={{ flexGrow: 0, width: 140, marginRight: '8px' }}>
          <SimpleSelect
            sx={{ width: 140 }}
            options={MATCH_OPTIONS}
            onChange={handleMatcherTypeChange}
            value={overrideType}
          />
        </div>

        <div style={{ flexGrow: 1 }}>
          <TextField
            sx={{
              // This style is used to position the error message without changing the layout
              // size which would mess up how it's centered
              '& p': {
                position: 'absolute',
                bottom: -16,
              },
            }}
            fullWidth
            value={localOverrideValue}
            onChange={handleLocalValueChange}
            error={regexError !== undefined}
            helperText={regexError !== undefined ? 'Invalid regex' : undefined}
            label={overrideType === 'ByRegex' ? 'Regex' : 'Value'}
          />
        </div>
      </Stack>

      <Stack direction="row" flexWrap="nowrap" alignItems="center" gap={1} flexGrow={2}>
        <div style={{ flexGrow: 0 }}>
          <TextAndBackgroundColorPicker color={color} onColorChange={handleColorChange} />
        </div>
        <div style={{ flexGrow: 1 }}>
          <TextField fullWidth value={localMessage} onChange={handleLocalMessageChange} label="Message" />
        </div>
        <div style={{ flexGrow: 0 }}>
          <Tooltip Content="Delete">
            <IconButton variant="tertiary" size="small" onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      </Stack>
    </Stack>
  );
};

export interface TableColumnStringOverridesEditorProps {
  overrides: StringFormatOverrides;
  onOverridesChange: (overrides: StringFormatOverrides) => void;
}

export const TableColumnStringOverridesEditor = ({
  overrides,
  onOverridesChange,
}: TableColumnStringOverridesEditorProps) => {
  const handleOverrideChange = useCallback(
    (override: StringFormatOverride, index: number) => {
      onOverridesChange(
        produce(overrides, (draftOverrides) => {
          if (draftOverrides.overrides && draftOverrides.overrides[index]) {
            draftOverrides.overrides[index] = override;
          }
        })
      );
    },
    [onOverridesChange, overrides]
  );

  const handleOverrideDelete = useCallback(
    (index: number) => {
      onOverridesChange(
        produce(overrides, (draftOverrides) => {
          if (draftOverrides.overrides && draftOverrides.overrides[index]) {
            draftOverrides.overrides.splice(index, 1);
          }
        })
      );
    },
    [onOverridesChange, overrides]
  );

  const handleAddOverride = useCallback(() => {
    onOverridesChange(
      produce(overrides, (draftOverrides) => {
        if (!draftOverrides.overrides) {
          draftOverrides.overrides = [];
        }

        draftOverrides.overrides.push({
          value: '',
          matcher: { type: 'ByName' },
          properties: {},
        });
      })
    );
  }, [onOverridesChange, overrides]);

  return (
    <Stack gap={2}>
      <Typography variant="h4">Color and message</Typography>
      {overrides.overrides?.map((override, index) => {
        return (
          <StringColumnSingleOverrideEditorWrapper
            key={index}
            index={index}
            override={override}
            onOverrideChange={handleOverrideChange}
            onDelete={handleOverrideDelete}
          />
        );
      })}

      <div>
        <Button variant="secondary" StartIcon={<PlusIcon />} sx={{ alignSelf: 'start' }} onClick={handleAddOverride}>
          Add rule
        </Button>
      </div>
    </Stack>
  );
};

const StringColumnSingleOverrideEditorWrapper = ({
  index,
  override,
  onDelete,
  onOverrideChange,
}: {
  index: number;
  override: StringFormatOverride;
  onOverrideChange: (override: StringFormatOverride, index: number) => void;
  onDelete: (index: number) => void;
}) => {
  const handleOverrideChange = useCallback(
    (override: StringFormatOverride) => {
      onOverrideChange(override, index);
    },
    [onOverrideChange, index]
  );

  const handleDelete = useCallback(() => onDelete(index), [onDelete, index]);

  return (
    <div data-testid="string-format-override-editor">
      <StringColumnSingleOverrideEditor
        override={override}
        onOverrideChange={handleOverrideChange}
        onDelete={handleDelete}
      />
    </div>
  );
};
